export const NODE_WIDTH = 280;
export const NODE_HEIGHT = 120;
export const NODE_HORIZONTAL_GAP = 40;
export const NODE_VERTICAL_GAP = 80;

export interface LayoutNode {
	id: string;
	x: number;
	y: number;
	width: number;
	height: number;
}

export function orderNodeIdsForTreeLayout(
	nodeIds: string[],
	rootIds: string[],
	cycleGroups: Array<{ memberIds: string[] }>
): string[] {
	const originalIndex = new Map(nodeIds.map((id, index) => [id, index]));
	const rootSet = new Set(rootIds);
	const cycleGroupByMember = new Map<string, { id: number; memberIds: string[] }>();

	cycleGroups.forEach((group, index) => {
		for (const memberId of group.memberIds) {
			if (rootSet.has(memberId) && originalIndex.has(memberId)) {
				cycleGroupByMember.set(memberId, { id: index, memberIds: group.memberIds });
			}
		}
	});

	const orderedRootIds: string[] = [];
	const addedRootIds = new Set<string>();
	const addedCycleGroupIds = new Set<number>();

	for (const id of nodeIds) {
		if (!rootSet.has(id)) continue;

		const cycleGroup = cycleGroupByMember.get(id);
		if (!cycleGroup) {
			orderedRootIds.push(id);
			addedRootIds.add(id);
			continue;
		}

		if (addedCycleGroupIds.has(cycleGroup.id)) continue;
		addedCycleGroupIds.add(cycleGroup.id);

		const visibleRootMembers = cycleGroup.memberIds
			.filter((memberId) => rootSet.has(memberId) && originalIndex.has(memberId))
			.sort((a, b) => originalIndex.get(a)! - originalIndex.get(b)!);

		for (const memberId of visibleRootMembers) {
			orderedRootIds.push(memberId);
			addedRootIds.add(memberId);
		}
	}

	return [...orderedRootIds, ...nodeIds.filter((id) => !addedRootIds.has(id))];
}

export function computeTreeLayout(
	nodeIds: string[],
	edges: Array<{ source: string; target: string }>,
	rootIds: string[]
): Map<string, LayoutNode> {
	const nodeSet = new Set(nodeIds);
	const childrenMap = new Map<string, string[]>();
	const parentSet = new Set<string>();

	for (const edge of edges) {
		if (!nodeSet.has(edge.source) || !nodeSet.has(edge.target)) continue;
		const children = childrenMap.get(edge.source);
		if (children) {
			children.push(edge.target);
		} else {
			childrenMap.set(edge.source, [edge.target]);
		}
		parentSet.add(edge.target);
	}

	const rootSet = new Set(rootIds.filter((id) => nodeSet.has(id)));
	for (const id of nodeIds) {
		if (!parentSet.has(id)) {
			rootSet.add(id);
		}
	}

	const rootOrder = nodeIds.filter((id) => rootSet.has(id));
	const subtreeWidths = new Map<string, number>();

	const getChildren = (id: string): string[] => childrenMap.get(id) ?? [];

	const computeSubtreeWidth = (id: string): number => {
		const children = getChildren(id);
		if (children.length === 0) {
			subtreeWidths.set(id, NODE_WIDTH);
			return NODE_WIDTH;
		}

		const childWidths = children.map((childId) => computeSubtreeWidth(childId));
		const childrenWidth =
			childWidths.reduce((sum, width) => sum + width, 0) + NODE_HORIZONTAL_GAP * (children.length - 1);
		const width = Math.max(NODE_WIDTH, childrenWidth);
		subtreeWidths.set(id, width);
		return width;
	};

	const positions = new Map<string, LayoutNode>();

	const assignPosition = (id: string, left: number, depth: number) => {
		const subtreeWidth = subtreeWidths.get(id) ?? NODE_WIDTH;
		const x = left + subtreeWidth / 2 - NODE_WIDTH / 2;
		const y = depth * (NODE_HEIGHT + NODE_VERTICAL_GAP);
		positions.set(id, {
			id,
			x,
			y,
			width: NODE_WIDTH,
			height: NODE_HEIGHT,
		});

		const children = getChildren(id);
		if (children.length === 0) return;

		const childrenWidth =
			children.reduce((sum, childId) => sum + (subtreeWidths.get(childId) ?? NODE_WIDTH), 0) +
			NODE_HORIZONTAL_GAP * (children.length - 1);
		let childLeft = left + (subtreeWidth - childrenWidth) / 2;

		for (const childId of children) {
			assignPosition(childId, childLeft, depth + 1);
			childLeft += (subtreeWidths.get(childId) ?? NODE_WIDTH) + NODE_HORIZONTAL_GAP;
		}
	};

	for (const rootId of rootOrder) {
		computeSubtreeWidth(rootId);
	}

	let nextRootLeft = 0;
	for (const rootId of rootOrder) {
		assignPosition(rootId, nextRootLeft, 0);
		nextRootLeft += (subtreeWidths.get(rootId) ?? NODE_WIDTH) + NODE_HORIZONTAL_GAP;
	}

	return positions;
}

export function computeBounds(positions: Map<string, LayoutNode>): {
	minX: number;
	minY: number;
	maxX: number;
	maxY: number;
	width: number;
	height: number;
} {
	let minX = Infinity;
	let minY = Infinity;
	let maxX = -Infinity;
	let maxY = -Infinity;

	for (const node of positions.values()) {
		minX = Math.min(minX, node.x);
		minY = Math.min(minY, node.y);
		maxX = Math.max(maxX, node.x + node.width);
		maxY = Math.max(maxY, node.y + node.height);
	}

	if (!isFinite(minX)) {
		return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
	}

	return {
		minX,
		minY,
		maxX,
		maxY,
		width: maxX - minX,
		height: maxY - minY,
	};
}
